import { Badge } from "@hub/components/ui/badge";
import { Button } from "@hub/components/ui/button";
import { useEffect, useRef, useState } from "react";
import { ApiError, apiFetch } from "./api";

/**
 * The fixed, HUB-OWNED phases of a create walk, in the order
 * @hub/server/provisioning-orchestrator writes them. The box's own
 * `bootstrapPhase` (free text, BOX-owned — see the schema's own comment on
 * why the two columns are kept separate) and the terminal `running` status
 * are rendered as two more steps after these, not folded into this list:
 * folding them in would let a box's arbitrary string index into an array
 * built for the hub's fixed phase names.
 */
const CREATE_PHASES = [
  "writing-config",
  "launching-ec2",
  "writing-dns",
  "registering-redirect-uri",
  "awaiting-enrollment",
] as const;

const PHASE_LABEL: Record<(typeof CREATE_PHASES)[number], string> = {
  "writing-config": "Writing config",
  "launching-ec2": "Launching EC2",
  "writing-dns": "Writing DNS",
  "registering-redirect-uri": "Registering redirect URI",
  "awaiting-enrollment": "Awaiting enrollment",
};

/** Statuses the background walk can still be moving through on its own,
 *  without any click from the operator — polled every 3s while true.
 *  Includes "terminating": `waitForSnapshot` alone allows up to 30 minutes,
 *  and without polling here the destroy card just sits static for however
 *  long that takes, with nothing telling the operator it is still working.
 *  Destroy still gets no live PHASE tracker (see `isDestroyRun` below) — only
 *  its plain status line refreshes. */
export const POLLING_STATUSES = new Set([
  "provisioning",
  "awaiting-enrollment",
  "bootstrapping",
  "terminating",
]);

/**
 * I3: `handleProvision` returns 202 before its claim UPDATE lands, and the
 * auto-`/provision` POST `AddInstancePage` fires right after create can fail
 * outright (a network blip, a 500, the operator closing the tab). Either way
 * leaves a row at `pending` with no `lastError` -- `pending` was not in
 * `POLLING_STATUSES` and Retry is gated on `lastError`, so that row rendered
 * an all-grey phase tracker with no error, no button, and no way to start
 * provisioning at all. Exported and pure for the same reason as
 * `POLLING_STATUSES` itself: no React component-rendering test setup here.
 */
export function canStartProvisioning(status: string): boolean {
  return status === "pending";
}

const POLL_MS = 3000;

export interface LatestRun {
  id: string;
  action: string;
  status: string;
  startedAt: string;
  finishedAt: string | null;
  log: string | null;
}

/** Exactly the fields this card needs off the detail payload.
 *  InstanceDetailPage's own instance type is a superset of this. */
export interface ProvisioningInstance {
  id: string;
  slug: string;
  status: string;
  provisioningPhase: string | null;
  bootstrapPhase: string | null;
  lastError: string | null;
  /** Null until the box has fetched its `.env`. Read for one thing only:
   *  whether the password below may be acknowledged yet. */
  enrolledAt: string | null;
  /** Present ONLY while the operator has never acknowledged it — see
   *  getInstanceDetail's own doc. Once shown, acknowledging clears it here
   *  and it must never come back. */
  adminPassword: string | null;
  latestRun: LatestRun | null;
}

/**
 * Mirrors the server's own gate in handleAcknowledgePassword
 * (@hub/routes/instances) — which is the enforcement; this only greys the
 * button out so an operator is never invited to make the one click that
 * strands their instance. A box that has not enrolled still needs this
 * password to build its `.env`, and the hub holds no second copy.
 */
export function canAcknowledgePassword(instance: ProvisioningInstance): boolean {
  return instance.enrolledAt !== null || instance.status === "terminated";
}

/**
 * Whether to offer Retry, which the server runs as a CREATE walk. A destroy
 * that did not finish leaves the row at `error` with a `lastError` -- the
 * same shape a failed create leaves -- so status alone would offer Retry for
 * it (hub-1). The server refuses that click (provisionRefusal); this keeps
 * the button from being offered at all, and the card points at Destroy.
 */
export function canRetryCreate(
  instance: Pick<ProvisioningInstance, "status" | "lastError" | "latestRun">,
): boolean {
  return (
    instance.status === "error" &&
    instance.lastError !== null &&
    instance.latestRun?.action !== "destroy"
  );
}

type StepState = "done" | "current" | "error" | "upcoming";

function StepDot({ state }: { state: StepState }) {
  const cls =
    state === "done"
      ? "border-[var(--success)] bg-[var(--success)]"
      : state === "current"
        ? "animate-pulse border-primary bg-primary/30"
        : state === "error"
          ? "border-destructive bg-destructive"
          : "border-border bg-background";
  return <span className={`size-2.5 shrink-0 rounded-full border-2 ${cls}`} aria-hidden="true" />;
}

function Connector() {
  return <span className="mx-1 h-px w-4 shrink-0 bg-border" aria-hidden="true" />;
}

/**
 * Tracker for a CREATE (or retry) walk. A destroy run has no phase sequence
 * of its own here — ProvisioningProgress renders a plain status line for
 * that case instead, see below.
 *
 * `provisioningPhase` only ever names one of CREATE_PHASES while the hub's
 * own walk is still moving; once the box has reported ANY bootstrapPhase, it
 * has enrolled, which means every create phase already succeeded — so an
 * `error` status alongside a non-null bootstrapPhase is a failure that
 * happened AFTER creation (a stale-phase sweep, a bootstrap timeout), and
 * must not paint "awaiting-enrollment" as the thing that failed.
 */
function CreatePhaseTracker({ instance }: { instance: ProvisioningInstance }) {
  const hasBootstrapped = instance.bootstrapPhase !== null;
  const currentIndex = instance.provisioningPhase
    ? CREATE_PHASES.indexOf(instance.provisioningPhase as (typeof CREATE_PHASES)[number])
    : -1;

  function stateFor(i: number): StepState {
    if (currentIndex === -1 || i > currentIndex) return "upcoming";
    if (i < currentIndex) return "done";
    // i === currentIndex — the phase currently (or most recently) held.
    if (instance.status === "error" && !hasBootstrapped) return "error";
    if (instance.status === "provisioning" || instance.status === "awaiting-enrollment") {
      return "current";
    }
    // bootstrapping, running, or an error that happened after enrollment —
    // this create phase itself already finished.
    return "done";
  }

  const bootstrapState: StepState =
    instance.status === "running"
      ? "done"
      : instance.status === "bootstrapping"
        ? "current"
        : instance.status === "error" && hasBootstrapped
          ? "error"
          : "upcoming";

  const runningState: StepState = instance.status === "running" ? "done" : "upcoming";

  return (
    <ol className="flex flex-wrap items-center gap-y-2">
      {CREATE_PHASES.map((phase, i) => {
        const state = stateFor(i);
        return (
          <li key={phase} className="flex items-center gap-1.5">
            {i > 0 ? <Connector /> : null}
            <StepDot state={state} />
            <span
              className={`text-xs ${state === "upcoming" ? "text-muted-foreground" : "font-medium"}`}
            >
              {PHASE_LABEL[phase]}
            </span>
          </li>
        );
      })}
      <li className="flex items-center gap-1.5">
        <Connector />
        <StepDot state={bootstrapState} />
        <span
          className={`text-xs ${bootstrapState === "upcoming" ? "text-muted-foreground" : "font-medium"}`}
        >
          {instance.status === "bootstrapping" && instance.bootstrapPhase
            ? `Bootstrapping — ${instance.bootstrapPhase}`
            : "Bootstrapping"}
        </span>
      </li>
      <li className="flex items-center gap-1.5">
        <Connector />
        <StepDot state={runningState} />
        <span
          className={`text-xs ${runningState === "upcoming" ? "text-muted-foreground" : "font-medium"}`}
        >
          Running
        </span>
      </li>
    </ol>
  );
}

/**
 * Live phase card for an instance that is not (yet, or no longer) `running`.
 * Owns three things a plain status field cannot: the phase tracker above,
 * the one-time break-glass password handoff, and the Retry action on a
 * failed walk.
 *
 * Deliberately stateless about `instance` itself — the parent
 * (InstanceDetailPage) holds it and passes the current value down; every
 * poll, retry, and password acknowledgement here calls `onUpdate` to push a
 * fresh copy back up rather than keeping a second, competing copy in this
 * component's own state.
 */
export function ProvisioningProgress({
  instance,
  onUpdate,
}: {
  instance: ProvisioningInstance;
  onUpdate: (instance: ProvisioningInstance) => void;
}) {
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [acking, setAcking] = useState(false);
  const [ackError, setAckError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState<string | null>(null);

  // Read through refs inside the interval so the effect does not need to
  // re-subscribe (and lose its 3s cadence) every time the parent re-renders
  // with a new `onUpdate` closure or a new `instance.id`.
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const idRef = useRef(instance.id);
  idRef.current = instance.id;

  const polling = POLLING_STATUSES.has(instance.status);

  useEffect(() => {
    if (!polling) return;
    const timer = setInterval(async () => {
      try {
        const body = await apiFetch<{ instance: ProvisioningInstance }>(
          `/api/instances/${idRef.current}`,
        );
        onUpdateRef.current(body.instance);
      } catch {
        // A transient fetch failure just waits for the next tick — the
        // operator keeps seeing the last-known state, which beats replacing
        // it with an error banner every 3 seconds on a flaky connection.
      }
    }, POLL_MS);
    return () => clearInterval(timer);
    // Re-subscribe only when polling starts or stops — `onUpdate` and
    // `instance.id` are read through the refs above precisely so this effect
    // has exactly one real dependency and does not need to tear down and
    // rebuild its 3s interval on every parent re-render.
  }, [polling]);

  /** I3's own fix: the only way to start (or restart) provisioning for a
   *  `pending` row from the console. `retry` above is for a row that has
   *  already tried and failed (gated on `lastError`) -- `pending` never has
   *  one, so it needs its own action rather than reusing Retry's gate. */
  async function startProvisioning() {
    setProvisioning(true);
    setProvisionError(null);
    try {
      await apiFetch(`/api/instances/${instance.id}/provision`, { method: "POST" });
      const body = await apiFetch<{ instance: ProvisioningInstance }>(
        `/api/instances/${instance.id}`,
      );
      onUpdate(body.instance);
    } catch (err) {
      setProvisionError(err instanceof ApiError ? err.message : "failed to start provisioning");
    } finally {
      setProvisioning(false);
    }
  }

  async function retry() {
    setRetrying(true);
    setRetryError(null);
    try {
      await apiFetch(`/api/instances/${instance.id}/retry`, { method: "POST" });
      const body = await apiFetch<{ instance: ProvisioningInstance }>(
        `/api/instances/${instance.id}`,
      );
      onUpdate(body.instance);
    } catch (err) {
      setRetryError(err instanceof ApiError ? err.message : "failed to retry");
    } finally {
      setRetrying(false);
    }
  }

  async function acknowledgePassword() {
    setAcking(true);
    setAckError(null);
    try {
      await apiFetch(`/api/instances/${instance.id}/acknowledge-password`, { method: "POST" });
      // The server has already nulled it — mirror that locally rather than
      // waiting for the next poll, so it disappears the instant it is saved.
      onUpdate({ ...instance, adminPassword: null });
    } catch (err) {
      setAckError(err instanceof ApiError ? err.message : "failed to acknowledge");
    } finally {
      setAcking(false);
    }
  }

  async function copyPassword() {
    if (!instance.adminPassword) return;
    try {
      await navigator.clipboard.writeText(instance.adminPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access can be denied by the browser; the password is still
      // plain, selectable text right below the button.
    }
  }

  const isDestroyRun = instance.latestRun?.action === "destroy";

  return (
    <div className="mb-8 space-y-4 rounded-xl border border-border bg-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Provisioning
        </h2>
        <Badge variant={instance.status === "error" ? "destructive" : "secondary"}>
          {instance.status}
        </Badge>
      </div>

      {isDestroyRun ? (
        <p className="text-sm text-muted-foreground">
          {instance.status === "error"
            ? "The destroy did not finish cleanly — see the log below."
            : `Destroy in progress (${instance.status}).`}
        </p>
      ) : (
        <CreatePhaseTracker instance={instance} />
      )}

      {canStartProvisioning(instance.status) ? (
        <div className="rounded-lg border border-border bg-muted/40 p-4">
          <p className="mb-2 text-sm text-muted-foreground">
            This instance has not started provisioning yet.
          </p>
          <Button type="button" size="sm" onClick={startProvisioning} disabled={provisioning}>
            {provisioning ? "Starting…" : "Provision"}
          </Button>
          {provisionError ? (
            <p className="mt-2 text-xs text-destructive">{provisionError}</p>
          ) : null}
        </div>
      ) : null}

      {instance.adminPassword ? (
        <div className="rounded-lg border border-[var(--warning)]/40 bg-[var(--warning-bg)] p-4">
          <p className="mb-2 text-sm font-medium text-[var(--warning)]">
            Break-glass admin password — shown once
          </p>
          <p className="mb-3 text-xs text-muted-foreground">
            Save this now. Once acknowledged, the hub can never show it again — recovering it after
            that means resetting it on the box itself.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <code className="select-all rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm">
              {instance.adminPassword}
            </code>
            <Button type="button" variant="outline" size="sm" onClick={copyPassword}>
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={acknowledgePassword}
              disabled={acking || !canAcknowledgePassword(instance)}
            >
              {acking ? "Saving…" : "I have saved this"}
            </Button>
          </div>
          {canAcknowledgePassword(instance) ? null : (
            <p className="mt-2 text-xs text-muted-foreground">
              Copy it now — you can dismiss this once the box has enrolled. The instance needs this
              password to build its own configuration, and clearing it first would strand it.
            </p>
          )}
          {ackError ? <p className="mt-2 text-xs text-destructive">{ackError}</p> : null}
        </div>
      ) : null}

      {/* Shown for ANY status, not just `error`: a box that cannot enrol
          records why on the row (see noteEnrollBlocked in @hub/routes/enroll)
          while the status is still `awaiting-enrollment`, and that message
          used to be invisible here for the full 25-minute window the box
          spends retrying. Retry itself stays gated on `error` — it is the
          orchestrator's claim that needs that status, not the message — and
          never offered after a destroy (see canRetryCreate). */}
      {instance.lastError ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4">
          <p className="mb-2 text-sm text-destructive">{instance.lastError}</p>
          {isDestroyRun && instance.status === "error" ? (
            <p className="text-xs text-muted-foreground">To finish it, run Destroy below again.</p>
          ) : null}
          {canRetryCreate(instance) ? (
            <>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={retry}
                disabled={retrying}
              >
                {retrying ? "Retrying…" : "Retry"}
              </Button>
              {retryError ? <p className="mt-2 text-xs text-destructive">{retryError}</p> : null}
            </>
          ) : null}
        </div>
      ) : null}

      {instance.latestRun ? (
        <div>
          <p className="mb-1 text-xs text-muted-foreground">
            {instance.latestRun.action} · {instance.latestRun.status} ·{" "}
            {new Date(instance.latestRun.startedAt).toLocaleString()}
            {instance.latestRun.finishedAt
              ? ` – ${new Date(instance.latestRun.finishedAt).toLocaleTimeString()}`
              : " (running)"}
          </p>
          <pre className="max-h-64 overflow-x-auto overflow-y-auto rounded-md border border-border bg-muted p-3 text-xs">
            {instance.latestRun.log || "No log output yet."}
          </pre>
        </div>
      ) : null}
    </div>
  );
}
